import {
  allocateStatPoints,
  cloneCharacterProgression,
  saveCharacterProgressionWithStatValidation,
  STAT_ALLOCATE_STEP,
  totalAllocatedStatPoints,
  type CharacterProgression,
  progressionXpDisplay,
} from "@/game/model/character-progression";
import {
  playerRatings,
  STAT_IDS,
  STAT_LABEL_TH,
  STAT_PLUS_HINT_TH,
  type StatAllocation,
  type StatId,
} from "@/game/model/character-stats";
import { HomeEvents } from "@/home/home-events";

function formatNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString("en-US");
}

export type StatPanelOptions = {
  compact?: boolean;
  getExtraBonus?: () => Partial<import("@/game/model/character-stats").StatAllocation> | undefined;
};

function draftIsDirty(draft: CharacterProgression, saved: CharacterProgression): boolean {
  if (draft.statPointsAvailable !== saved.statPointsAvailable) return true;
  return STAT_IDS.some((id) => draft.allocated[id] !== saved.allocated[id]);
}

export function mountStatAllocationPanel(
  host: HTMLElement,
  userId: string,
  getProgression: () => CharacterProgression,
  onUpdate: (next: CharacterProgression) => void,
  options: StatPanelOptions = {},
): () => void {
  const compact = options.compact ?? false;
  const getExtraBonus = options.getExtraBonus;

  let saved = cloneCharacterProgression(getProgression());
  let draft = cloneCharacterProgression(saved);
  let statusMessage = "";
  let statusIsError = false;

  const syncSavedFromParent = () => {
    saved = cloneCharacterProgression(getProgression());
    if (!draftIsDirty(draft, saved)) {
      draft = cloneCharacterProgression(saved);
    }
  };

  const render = () => {
    syncSavedFromParent();
    const progression = draft;
    const dirty = draftIsDirty(draft, saved);
    const xp = progressionXpDisplay(progression);
    const ratings = playerRatings(
      progression,
      getExtraBonus?.() as Partial<StatAllocation> | undefined,
    );
    const ap = progression.statPointsAvailable;
    const rows = STAT_IDS
      .map((id) => {
        const pts = progression.allocated[id];
        const hint = STAT_PLUS_HINT_TH[id];
        return `
          <div class="stat-row" data-stat-id="${id}">
            <span class="stat-row-label" title="${hint}">${STAT_LABEL_TH[id]}</span>
            <span class="stat-row-pts" title="แต้มที่ลง">${pts}</span>
            <div class="stat-row-actions">
              <button type="button" class="btn btn-ghost btn-xs stat-row-plus stat-row-plus--one" data-stat-plus="${id}" data-stat-plus-amount="1" title="${hint}" ${
                ap > 0 ? "" : "disabled"
              }>+1</button>
              <button type="button" class="btn btn-ghost btn-xs stat-row-plus" data-stat-plus="${id}" data-stat-plus-amount="${STAT_ALLOCATE_STEP}" title="${hint}" ${
                ap >= STAT_ALLOCATE_STEP ? "" : "disabled"
              }>+${STAT_ALLOCATE_STEP}</button>
            </div>
          </div>`;
      })
      .join("");

    const xpBar =
      xp.xpToNext > 0
        ? `<div class="stat-xp-bar" role="progressbar" aria-valuenow="${xp.xpIntoLevel}" aria-valuemax="${xp.xpToNext}">
            <div class="stat-xp-fill" style="width:${Math.min(100, (xp.xpIntoLevel / xp.xpToNext) * 100).toFixed(1)}%"></div>
          </div>`
        : `<p class="hint stat-xp-max">เลเวลเต็ม</p>`;

    const dirtyBadge = dirty
      ? `<span class="stat-panel-draft-badge">ยังไม่บันทึก</span>`
      : "";

    const statusBlock =
      statusMessage
        ? `<p class="hint stat-panel-status${statusIsError ? " stat-panel-status--error" : ""}" role="status">${statusMessage}</p>`
        : "";

    const actions = dirty
      ? `
        <div class="stat-panel-actions">
          <button type="button" class="btn btn-ghost btn-sm" data-stat-discard>ยกเลิก</button>
          <button type="button" class="btn btn-primary btn-sm" data-stat-save>บันทึก</button>
        </div>`
      : "";

    host.innerHTML = `
      <section class="stat-panel${compact ? " stat-panel--compact" : ""}${dirty ? " stat-panel--dirty" : ""}" aria-label="สเตตตัวละคร">
        <div class="stat-panel-head">
          <span class="stat-panel-lv">Lv ${progression.level}</span>
          <span class="stat-panel-ap" title="แต้มอัปคงเหลือ">AP ${ap} ${dirtyBadge}</span>
        </div>
        <p class="stat-panel-exp">EXP สะสม ${formatNum(xp.totalExp)}</p>
        ${xpBar}
        <p class="stat-panel-derived hint">
          โจมตี ${ratings.attack} · ป้องกัน ${ratings.defense} · คริ ${(ratings.critChance * 100).toFixed(1)}% ×${ratings.critMultiplier.toFixed(2)} · ฟื้น ${ratings.hpRegenPerSec.toFixed(2)}/วิ
        </p>
        <p class="hint stat-panel-plus-hint">+1 / +${STAT_ALLOCATE_STEP} ใช้ AP ตามปุ่ม · แก้แล้วกดบันทึก</p>
        <div class="stat-rows">${rows}</div>
        ${statusBlock}
        ${actions}
      </section>
    `;
  };

  const onClick = (event: Event) => {
    const target = event.target as HTMLElement;

    if (target.closest("[data-stat-discard]")) {
      draft = cloneCharacterProgression(saved);
      statusMessage = "";
      statusIsError = false;
      render();
      return;
    }

    if (target.closest("[data-stat-save]")) {
      const spentDelta = totalAllocatedStatPoints(draft.allocated) - totalAllocatedStatPoints(saved.allocated);
      const apAfter = draft.statPointsAvailable;
      const summary =
        spentDelta > 0
          ? `ลงสเตตรวม +${spentDelta} แต้ม · AP คงเหลือ ${apAfter}`
          : `AP คงเหลือ ${apAfter}`;
      const ok = window.confirm(`ยืนยันบันทึกการลงสเตต?\n${summary}`);
      if (!ok) return;

      const result = saveCharacterProgressionWithStatValidation(userId, draft);
      if (!result.ok) {
        statusMessage = result.message;
        statusIsError = true;
        render();
        return;
      }
      saved = cloneCharacterProgression(draft);
      onUpdate(draft);
      window.dispatchEvent(
        new CustomEvent(HomeEvents.ProgressionChanged, { detail: { progression: draft } }),
      );
      statusMessage = "บันทึกแล้ว";
      statusIsError = false;
      render();
      return;
    }

    const plusBtn = target.closest<HTMLElement>("[data-stat-plus]");
    const id = plusBtn?.dataset.statPlus as StatId | undefined;
    if (!id || !plusBtn) return;
    const amount = Math.max(1, Math.floor(Number(plusBtn.dataset.statPlusAmount) || STAT_ALLOCATE_STEP));
    if (draft.statPointsAvailable < amount) {
      statusMessage = `AP ไม่พอ (ต้องมีอย่างน้อย ${amount})`;
      statusIsError = true;
      render();
      return;
    }
    const next = allocateStatPoints(draft, id, amount);
    if (next === draft) return;
    draft = next;
    statusMessage = "";
    statusIsError = false;
    render();
  };

  host.addEventListener("click", onClick);
  render();

  return () => {
    host.removeEventListener("click", onClick);
    host.innerHTML = "";
  };
}
